// @flow

import React from 'react';
import { partial } from 'lodash';

import { HostedField } from '@zapier/payment/src/components/CreditCardForm/HostedFields';
import {
  CountrySelect,
  PaymentFormField,
  PostalCodeInput,
  StateSelect,
} from '@zapier/payment';
import { GridLayout, Media, TextInput } from '@zapier/common-components';
import {
  getValue,
  handleChange,
  hasError,
} from '@zapier/payment/src/utils/formHelpers';

import type {
  FieldName,
  Fields,
  SensitiveValue,
} from '@zapier/payment/src/types';

type Props = {
  /** Fields to show */
  fields: Fields,
  /** Call when field changes value */
  onChange: (name: FieldName, value: string | SensitiveValue) => void,
};

class CreditCardFieldset extends React.Component<Props> {
  id = Math.random() * 100;

  getLabelId = (name: string): string => `${name}-label-${this.id}`;
  getInputId = (name: string): string => `${name}-input-${this.id}`;

  render() {
    const { fields, onChange } = this.props;

    const showAdditionalFields = getValue('addressCountry', fields) === 'US';

    return (
      <Media queries={{ small: '(max-width: 750px)' }}>
        {({ small }) => (
          <GridLayout columns={4} gap={10}>
            <PaymentFormField
              id={this.getLabelId('name')}
              label="Name"
              span={4}
            >
              <TextInput
                autoComplete="shipping name"
                hasError={hasError('name', fields)}
                id={this.getInputId('name')}
                name="name"
                onChange={handleChange(onChange, 'name')}
                placeholder="Full name"
                value={getValue('name', fields)}
              />
            </PaymentFormField>
            <PaymentFormField label="Number" span={small ? 4 : 2}>
              <HostedField
                hasError={hasError('number', fields)}
                onChange={partial(onChange, 'number')}
                type="number"
              />
            </PaymentFormField>
            <PaymentFormField label="Expiry" span={small ? 2 : 1}>
              <HostedField
                hasError={hasError('expiry', fields)}
                onChange={partial(onChange, 'expiry')}
                type="expiry"
              />
            </PaymentFormField>
            <PaymentFormField label="CVC" span={small ? 2 : 1}>
              <HostedField
                hasError={hasError('cvc', fields)}
                onChange={partial(onChange, 'cvc')}
                type="cvc"
              />
            </PaymentFormField>
            <PaymentFormField
              id={this.getLabelId('country')}
              label="Country"
              span={small ? 4 : 2}
            >
              <CountrySelect
                hasError={hasError('addressCountry', fields)}
                labelId={this.getLabelId('country')}
                onChange={handleChange(onChange, 'addressCountry')}
                value={getValue('addressCountry', fields)}
              />
            </PaymentFormField>
            <PaymentFormField
              id={this.getLabelId('state')}
              isHidden={!showAdditionalFields}
              label="State"
              span={small ? 4 : 1}
            >
              <StateSelect
                hasError={hasError('addressState', fields)}
                labelId={this.getLabelId('state')}
                onChange={handleChange(onChange, 'addressState')}
                value={getValue('addressState', fields)}
              />
            </PaymentFormField>
            <PaymentFormField
              id={this.getLabelId('addressZip')}
              isHidden={!showAdditionalFields}
              label="Zip Code"
              span={small ? 2 : 1}
            >
              <PostalCodeInput
                hasError={hasError('addressZip', fields)}
                id={this.getInputId('addressZip')}
                labelId={this.getLabelId('addressZip')}
                onChange={handleChange(onChange, 'addressZip')}
                value={getValue('addressZip', fields)}
              />
            </PaymentFormField>
          </GridLayout>
        )}
      </Media>
    );
  }
}

export default CreditCardFieldset;
